import React, { useMemo, useState, useCallback } from 'react'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import GradingState from '@/components/organisms/Grading/GradingState'
import { Button, Radio } from '@/components/atoms'
import { Dialog } from '@/components/organisms/Modal/Dialog'
import RecordingGradingContents from '@/components/Modal/RecordingGradingContents'
import { academyLearningScheduleApi } from 'services/api'

interface GradingStoryDialogQustionProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  responseJson?: string
  manualGradingResults?: Record<number, 'correct' | 'incorrect' | 'grading'>
  onManualGradingChange?: (questionIdx: number, status: 'correct' | 'incorrect' | undefined) => void
  canUseAi?: boolean
  lockedManualKeys?: Set<string>
}

const GradingStoryDialogQustion = ({ type = 'scoreCard', activity, responseJson, manualGradingResults, onManualGradingChange, canUseAi = true, lockedManualKeys }: GradingStoryDialogQustionProps) => {
  // 1) 문제(질문) 파싱
  const questions: Array<{ imageUrl: string; question: string; correctAnswer: string; audioUrl: string | null }> = useMemo(() => {
    try {
      const items = activity?.items || []
      return items.map((it: any) => {
        const c = it?.contentJson ? (typeof it.contentJson === 'string' ? JSON.parse(it.contentJson) : it.contentJson) : {}
        return {
          imageUrl: c.imageUrl || c.wordImageUrl || '',
          question: c.sentence || c.question || '',
          correctAnswer: c.answer || '',
          audioUrl: c.audioUrl || null
        }
      })
    } catch (e) {
      console.error('StoryDialogQ 파싱 실패', e)
      return [] as any[]
    }
  }, [activity])

  // 2) 응답 파싱: questionAnswers + 녹음
  const { studentAnswers, recordingUrls } = useMemo((): { studentAnswers: string[]; recordingUrls: (string | null)[] } => {
    let resp: any = {}
    try { if (responseJson) resp = JSON.parse(responseJson) } catch {}
    const answers: string[] = []
    const recs: (string | null)[] = []

    if (resp?.questionAnswers) {
      questions.forEach((_: any, idx: number) => {
        const qa = resp.questionAnswers[`question-${idx}`]
        const textAns = typeof qa?.textAnswer === 'string' ? qa.textAnswer : ''
        const recUrl = typeof qa?.recordingUrl === 'string' ? qa.recordingUrl : (typeof qa?.url === 'string' ? qa.url : null)
        answers.push(textAns)
        recs.push(recUrl)
      })
    } else {
      // 대체: 배열/인덱스 기반 구조 지원
      questions.forEach((_: any, idx: number) => {
        const e = resp?.[idx] || resp?.[String(idx)] || {}
        answers.push(typeof e?.answer === 'string' ? e.answer : (typeof e?.userInput === 'string' ? e.userInput : ''))
        recs.push(typeof e?.recordingUrl === 'string' ? e.recordingUrl : null)
      })
    }
    return { studentAnswers: answers, recordingUrls: recs }
  }, [responseJson, questions])

  // 3) 상태: 정오답/다이얼로그
  const normalize = (s: string) => s.replace(/[^a-z0-9\s']+/gi, '').trim().toLowerCase()
  const computedStatuses: Array<'correct' | 'incorrect' | 'grading'> = useMemo(() => {
    return questions.map((q: { correctAnswer: string }, i: number) => {
      const s = studentAnswers[i] || ''
      if (!s) return 'grading'
      if (!q.correctAnswer) return 'grading'
      return normalize(s) === normalize(q.correctAnswer) ? 'correct' : 'incorrect'
    })
  }, [questions, studentAnswers])

  const [manualStatuses, setManualStatuses] = useState<Array<'correct' | 'incorrect' | null>>(() => {
    // 저장된 결과가 있으면 복원
    if (manualGradingResults && Object.keys(manualGradingResults).length > 0) {
      return questions.map((_, idx) => {
        const result = manualGradingResults[idx];
        return result === 'correct' ? 'correct' : (result === 'incorrect' ? 'incorrect' : null);
      });
    }
    return questions.map(() => null);
  })
  
  // 저장된 결과가 변경되면 상태 업데이트
  React.useEffect(() => {
    if (manualGradingResults && Object.keys(manualGradingResults).length > 0) {
      const newStatuses = questions.map((_, idx) => {
        const result = manualGradingResults[idx];
        return result === 'correct' ? 'correct' : (result === 'incorrect' ? 'incorrect' : null);
      });
      setManualStatuses(newStatuses);
    }
  }, [manualGradingResults, questions]);
  
  // 채점 화면에서는 초기 상태를 항상 '채점중'으로 표시 (자동 isCorrect 무시)
  const displayStatus = (idx: number) => {
    // 저장된 결과 우선
    if (manualGradingResults && manualGradingResults[idx]) {
      return manualGradingResults[idx] === 'correct' ? 'correct' : 'incorrect';
    }
    if (manualStatuses[idx]) return manualStatuses[idx] === 'correct' ? 'correct' : 'incorrect'
    return type === 'gradingCard' ? 'grading' : computedStatuses[idx]
  }

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogIndex, setDialogIndex] = useState<number | null>(null)
  const [dialogStatus, setDialogStatus] = useState<'correct' | 'incorrect'>('correct')
  const [dialogLocked, setDialogLocked] = useState(false)
  const [aiLoadingSet, setAiLoadingSet] = useState<Set<number>>(new Set())
  const activityId = activity?.id != null ? String(activity.id) : null;
  const isQuestionLocked = useCallback((idx: number) => {
    if (!activityId) return false;
    const variants = [`${activityId}-question-${idx}`, `sdq-${activityId}-${idx}`];
    return variants.some((key) => lockedManualKeys?.has(key));
  }, [activityId, lockedManualKeys]);
  const openDialog = (idx: number) => { setDialogIndex(idx); setDialogStatus(displayStatus(idx) === 'incorrect' ? 'incorrect' : 'correct'); setDialogLocked(isQuestionLocked(idx)); setDialogOpen(true) }
  const closeDialog = () => setDialogOpen(false)
  const confirmDialog = () => { if (dialogIndex!==null){ setManualStatuses(prev=>{ const n=[...prev]; n[dialogIndex]=dialogStatus; return n}); closeDialog(); } }

  const handleAiGrade = async (idx: number) => {
    if (isQuestionLocked(idx)) return;
    if (!canUseAi) {
      alert('AI 채점은 플러스 요금제 전용입니다.');
      return;
    }
    try {
      const recordingUrl = recordingUrls[idx]
      // 녹음이 없으면 백엔드에서 오답 처리하므로 그대로 요청
      const scheduleId = (window as any).__CURRENT_SCHEDULE_ID__
      setAiLoadingSet(prev => { const s = new Set(prev); s.add(idx); return s })
      const res = await academyLearningScheduleApi.aiGradeDictation(scheduleId, {
        recordingUrl: recordingUrl || '',
        correctWord: questions[idx]?.correctAnswer || ''
      })
      const correct = !!(res?.data?.correct)
      setManualStatuses(s => { const n=[...s]; n[idx] = correct ? 'correct' : 'incorrect'; return n })
      onManualGradingChange?.(idx, correct ? 'correct' : 'incorrect')
    } catch {
      alert('AI 채점에 실패했습니다.')
    } finally {
      setAiLoadingSet(prev => { const s = new Set(prev); s.delete(idx); return s })
    }
  }

  // 테두리 색상 결정 함수
  const getBorderColor = (status: 'correct' | 'incorrect' | 'grading') => {
    switch (status) {
      case 'correct':
        return 'border-green-500';
      case 'incorrect':
        return 'border-red-500';
      case 'grading':
        return 'border-gray-200';
      default:
        return 'border-gray-200';
    }
  };

  return (
    <div>
        <div className='flex flex-col gap-2'>
            <p className='text-lg font-bold'>스토리앤 다이얼로그 퀘스천</p>
            {questions.map((q: { imageUrl: string; question: string; correctAnswer: string; audioUrl: string | null }, idx: number) => {
              const questionLocked = isQuestionLocked(idx);
              return (
              <div key={`q-${idx}`} className={`overflow-hidden rounded-2xl border ${getBorderColor(displayStatus(idx))}`}>
                {/* 질문 영역 */}
                <div className='flex justify-between items-center px-4 py-2'>
                  <div className='flex items-center gap-2'>
                    <span className='flex justify-center items-center text-sm font-bold text-white bg-indigo-500 rounded-full size-6'>Q</span>
                    <p className='text-base font-bold'>{q.question || `질문 ${idx+1}`}</p>
                  </div>
                  {q.audioUrl && (
                    <ListenButton audioSrc={q.audioUrl} size={40} className='!p-1' />
                  )}
                </div>
                {/* 이미지가 있으면 표시 */}
                {q.imageUrl && (
                  <div className='px-4 pb-2'>
                    <img src={q.imageUrl} alt='question' className='rounded-xl max-h-48 object-cover' />
                  </div>
                )}
                {/* 답변 + 채점 */}
                <div className='flex justify-between items-center px-4 py-2'>
                  <p className='text-sm font-bold text-gray-500'>입력한 답</p>
                  <GradingState status={displayStatus(idx)} />
                </div>
                <div className='px-4 pb-3'>
                  <div className='px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-base text-gray-700'>
                    {studentAnswers[idx] || '학생이 입력한 답변 내용이 출력'}
                  </div>
                </div>
                {displayStatus(idx) === 'incorrect' && (
                  <div className='px-4 pb-3'>
                    <div className='px-4 py-3 bg-white rounded-xl border-2 border-red-300 text-base font-bold text-gray-800 text-center'>
                      정답 내용 출력
                      <div className='mt-1 font-normal text-gray-700'>{q.correctAnswer || '-'}</div>
                    </div>
                  </div>
                )}
                <div className='flex justify-between items-center px-4 pb-4'>
                  <div className='flex items-center gap-2'>
                    {type === 'gradingCard' && !questionLocked && (
                      <>
                        <Radio label='정답' name={`sdq-grading-${idx}`} onChange={() => {
                          setManualStatuses(s => { const n=[...s]; n[idx]='correct'; return n });
                          onManualGradingChange?.(idx, 'correct');
                        }} checked={manualStatuses[idx] === 'correct'} />
                        <Radio label='오답' name={`sdq-grading-${idx}`} onChange={() => {
                          setManualStatuses(s => { const n=[...s]; n[idx]='incorrect'; return n });
                          onManualGradingChange?.(idx, 'incorrect');
                        }} checked={manualStatuses[idx] === 'incorrect'} />
                      </>
                    )}
                  </div>
                  <div className='flex gap-2'>
                    {recordingUrls[idx] && (
                      <>
                        {type === 'gradingCard' && !questionLocked && (
                          <Button size='small' intent='secondary' onClick={() => openDialog(idx)}>녹음 듣고 채점하기</Button>
                        )}
                        {(type !== 'gradingCard' || questionLocked) && (
                          <Button size='small' intent='secondary' onClick={() => openDialog(idx)}>녹음 듣기</Button>
                        )}
                      </>
                    )}
                    {type === 'gradingCard' && !questionLocked && (
                      <>
                        {canUseAi && (
                          <Button size='small' onClick={() => handleAiGrade(idx)} disabled={aiLoadingSet.has(idx)}>
                            {aiLoadingSet.has(idx) ? '채점중...' : 'AI 채점'}
                          </Button>
                        )}
                        {manualStatuses[idx] && (
                          <Button size='small' intent='secondary' onClick={() => {
                            setManualStatuses(s => { const n=[...s]; n[idx]=null; return n })
                            onManualGradingChange?.(idx, undefined)
                          }}>초기화</Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
        </div>
    {/* 녹음 듣기 다이얼로그 */}
    <Dialog open={dialogOpen} onCancel={closeDialog} onOk={(type === 'gradingCard' && !dialogLocked) ? confirmDialog : closeDialog} okText={(type === 'gradingCard' && !dialogLocked) ? '확인' : '닫기'} cancelText={(type === 'gradingCard' && !dialogLocked) ? '취소' : undefined}>
      <RecordingGradingContents
        word={dialogIndex !== null ? (questions[dialogIndex]?.question || '') : ''}
        recordingUrl={dialogIndex !== null ? (recordingUrls[dialogIndex] || undefined) : undefined}
        initialGradingStatus={dialogStatus}
        onGradingChange={(type === 'gradingCard' && !dialogLocked) ? setDialogStatus : undefined}
        readOnly={type !== 'gradingCard' || dialogLocked}
      />
    </Dialog>
    </div>
  )
}

export default GradingStoryDialogQustion